import moment from 'moment/moment'

import { PSApplication } from '../components/app/PSApplication'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { BookableColorKey, getDefaultBlockColorKey } from '../utils/calendarColors'
import { BaseRecord } from './BaseRecord'
import { Bookable, ScheduleState } from './Bookable'
import { Resource } from './Resource'
import { FKType } from './SQLBuilder'
import { Schedule } from './Schedule'
import { User } from './User'

type BlockConstructorArgs = ConstructorArgs<() => Block>

export class Block extends BaseRecord implements Bookable {
    readonly recordType = 'block'

    readonly iBookable = true

    date: moment.Moment | undefined
    type: string | undefined
    title: string | undefined
    cancelledDate: moment.Moment | undefined
    duration: number | undefined

    constructor(data?: Partial<BlockConstructorArgs>) {
        super(data)
        if (!data) {
            return
        }

        this.type = data.type ?? undefined
        this.title = data.title ?? undefined
        this.date = this.propertyFromTimestampArg(data.date)
        this.duration = this.propertyFromIntArg(data.duration)
        this.cancelledDate = this.propertyFromTimestampArg(data.cancelled_date)
    }

    clone(overrides?: Partial<BlockConstructorArgs>): Block {
        return new Block({ ...this.serializeForClone(), ...overrides })
    }

    get description(): string {
        return `${this.date?.format()} - ${this.endDate?.format()}`
    }

    get endDate(): moment.Moment | undefined {
        return this.date?.clone().add(this.duration, 'minutes')
    }

    set endDate(endDate: moment.Moment | undefined) {
        if (endDate && this.date) this.duration = endDate.diff(this.date, 'minutes')
    }

    get confirmedDate(): moment.Moment | undefined {
        return undefined
    }

    get memo(): string | undefined {
        return undefined
    }

    get location(): string | undefined {
        return undefined
    }

    get shortTitle(): string | undefined {
        return this.title
    }

    get hasSchedule(): boolean {
        return this.schedule !== undefined && this.schedule.createdDate !== undefined
    }

    get isRecurring(): boolean {
        return (
            this.schedule !== undefined &&
            this.schedule.interval !== undefined &&
            this.schedule.interval > 0
        )
    }

    get scheduleStates(): ScheduleState[] {
        const states: ScheduleState[] = []
        states.push(ScheduleState.Editable)
        states.push(ScheduleState.Cancelable)
        return states
    }

    // Bookable interface methods
    get color(): BookableColorKey {
        return getDefaultBlockColorKey()
    }

    get employee(): User | undefined {
        return undefined
    }

    get allowJoin(): boolean {
        return false
    }

    get allowCheckin(): boolean {
        return false
    }

    get allowNotify(): boolean {
        return false
    }

    get ownerIsMe(): boolean {
        return this.owner != undefined && this.owner.id === PSApplication.me().id
    }

    deserialize(data: any) {
        super.deserialize(data)
        if (data.owner) this.owner = BaseRecord.fromSerializer(data.owner, User)
        if (data.creator) this.creator = BaseRecord.fromSerializer(data.creator, User)
        if (data.person) this.person = BaseRecord.fromSerializer(data.person, User)
        if (data.resource) this.resource = BaseRecord.fromSerializer(data.resource, Resource)
        if (data.schedule) this.schedule = BaseRecord.fromSerializer(data.schedule, Schedule)
        if (data.date) this.date = moment(data.date)
        if (data.cancelledDate) this.cancelledDate = moment(data.cancelledDate)
    }

    static fk: FKType = {
        users: ['owner', 'creator', 'person'],
        schedules: ['schedule'],
        resources: ['resource'],
    }

    jsonObject(): any {
        const dict = super.jsonObject()
        if (this.title) dict['title'] = this.title
        dict['date'] = this.date?.format()
        dict['duration'] = this.duration
        if (this.owner) dict['owner'] = this.owner.id
        if (this.creator) dict['creator'] = this.creator.id
        if (this.person) dict['person'] = this.person.id
        if (this.resource) dict['resource'] = this.resource.id
        if (this.schedule) {
            if (this.schedule.id) dict['schedule'] = this.schedule.id
            dict['instances'] = this.schedule.instances ?? ''
            if (this.schedule.interval && this.schedule.interval > 0) {
                dict['dow'] = this.schedule.dow
                dict['interval'] = this.schedule.interval
                dict['frequency'] = this.schedule.frequency
                if (this.schedule.wom && this.schedule.wom !== 0) dict['wom'] = this.schedule.wom
            }
        }
        return dict
    }

    static dataType(): DataType {
        return 'blocks'
    }

    get owner(): User | undefined {
        return this.getForeignRecord('owner', User)
    }
    set owner(owner: User | undefined) {
        this.setForeignRecord('owner', owner)
    }
    get creator(): User | undefined {
        return this.getForeignRecord('creator', User)
    }
    set creator(creator: User | undefined) {
        this.setForeignRecord('creator', creator)
    }
    get person(): User | undefined {
        return this.getForeignRecord('person', User)
    }
    set person(person: User | undefined) {
        this.setForeignRecord('person', person)
    }
    get resource(): Resource | undefined {
        return this.getForeignRecord('resource', Resource)
    }
    set resource(resource: Resource | undefined) {
        this.setForeignRecord('resource', resource)
    }
    get schedule(): Schedule | undefined {
        return this.getForeignRecord('schedule', Schedule)
    }
    set schedule(schedule: Schedule | undefined) {
        this.setForeignRecord('schedule', schedule)
    }
}
